import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Grid, Box, CircularProgress, Checkbox, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getBoxStorageHistoryByCustomerIdAction } from "../../../../redux/actions/boxStorageAction";
import { getBillItemIdListAction } from "../../../../redux/actions/cartAction";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import {
  IBoxStorageHistoryInvoice,
  IBoxStorageHistoryRow,
} from "../../../../typings/interfaces/responses/boxStorageResponse";
import { Colors } from "../../../../configs";
import { formatCurrency } from "../../../../util/common-functions";
import InvoiceHistoryModal from "../../../pos/invoice-history-modal/invoice-history-modal";
import BoxStorageHistoryTableRow from "./box-storage-history-table-row";

export interface IBoxStorageHistoryTableProps {
  customerId: string | undefined;
  height?: string;
  contentHeight?: string;
  onSelectionChange?(selected: IBoxStorageHistoryRow[]): void;
  refreshKey?: number;
}

const BoxStorageHistoryTable: FC<IBoxStorageHistoryTableProps> = ({
  customerId,
  height = "28.42rem",
  contentHeight = "21.92rem",
  onSelectionChange,
  refreshKey,
}) => {
  const dispatch = useAppDispatch();
  const { cartHistorySelectedRows, billItemIdList } = useAppSelector(
    (state) => state.cart
  );

  const [rows, setRows] = useState<IBoxStorageHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [openModal, setOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    useState<IBoxStorageHistoryInvoice | null>(null);

  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    if (!customerId) return;

    let cancelled = false;
    setLoading(true);

    dispatch(getBoxStorageHistoryByCustomerIdAction(Number(customerId)))
      .unwrap()
      .then((res: any) => {
        if (cancelled) return;
        setRows(res?.body ?? []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customerId, dispatch, refreshKey]);

  const toggleExpand = (configId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(configId) ? next.delete(configId) : next.add(configId);
      return next;
    });
  };

  // a box's invoices are pickable once selected; skip anything already
  // handed over or that can't be picked up (voided/refunded)
  const isInvoicePickupEligible = (invoice: IBoxStorageHistoryInvoice) =>
    !invoice.isVoided &&
    !invoice.isRefunded &&
    invoice.pickingFilterStatus !== "PICKED_UP";

  const addBoxesToPickupSelection = (boxesToAdd: IBoxStorageHistoryRow[]) => {
    const alreadySelectedBillIds = new Set(
      cartHistorySelectedRows.map((row) => row.billId)
    );
    const newInvoices = boxesToAdd
      .flatMap((row) => row.invoices)
      .filter(
        (invoice) =>
          isInvoicePickupEligible(invoice) &&
          !alreadySelectedBillIds.has(invoice.billId)
      );

    if (newInvoices.length === 0) return;

    newInvoices.forEach((invoice) =>
      dispatch(getBillItemIdListAction(invoice.id))
    );
    dispatch(
      CartSliceAction.setCartHistorySelectedRows([
        ...cartHistorySelectedRows,
        ...newInvoices,
      ])
    );
  };

  const removeBoxesFromPickupSelection = (
    boxesToRemove: IBoxStorageHistoryRow[]
  ) => {
    const removedBillIds = new Set(
      boxesToRemove.flatMap((row) => row.invoices.map((inv) => inv.billId))
    );
    const removedIds = new Set(
      boxesToRemove.flatMap((row) => row.invoices.map((inv) => inv.id))
    );

    if (removedBillIds.size === 0) return;

    dispatch(
      CartSliceAction.setBillItemIdList(
        billItemIdList.filter((item) => !removedIds.has(item.billId))
      )
    );
    dispatch(
      CartSliceAction.setCartHistorySelectedRows(
        cartHistorySelectedRows.filter((row) => !removedBillIds.has(row.billId))
      )
    );
  };

  const toggleSelect = (configId: number) => {
    const row = rows.find((r) => r.configId === configId);
    const isCurrentlySelected = selectedIds.has(configId);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      isCurrentlySelected ? next.delete(configId) : next.add(configId);
      return next;
    });

    if (!row) return;
    isCurrentlySelected
      ? removeBoxesFromPickupSelection([row])
      : addBoxesToPickupSelection([row]);
  };

  const toggleSelectAll = () => {
    const selectingAll = selectedIds.size !== rows.length;
    setSelectedIds(
      selectingAll ? new Set(rows.map((row) => row.configId)) : new Set()
    );

    selectingAll
      ? addBoxesToPickupSelection(rows)
      : removeBoxesFromPickupSelection(rows);
  };

  useEffect(() => {
    onSelectionChange?.(rows.filter((row) => selectedIds.has(row.configId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedIds]);

  // bills checked on the Invoices tab that aren't already sitting in a box here
  // are pending assignment (as opposed to bills checked via the box checkboxes
  // above, which are already boxed and headed for pickup instead)
  const alreadyBoxedBillIds = new Set(
    rows.flatMap((row) => row.invoices.map((inv) => inv.billId))
  );
  const pendingAssignmentBills = cartHistorySelectedRows.filter(
    (row) => !alreadyBoxedBillIds.has(row.billId)
  );

  // shrink the table to make room for the banner above it (its height varies
  // with how many invoices are selected/wrapped), so the whole thing keeps
  // fitting the fixed height/contentHeight budget handed down by the parent
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el || pendingAssignmentBills.length === 0) {
      setBannerHeight(0);
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setBannerHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [pendingAssignmentBills.length > 0]);

  const bannerSpace = bannerHeight > 0 ? `${bannerHeight}px + 2.71rem` : "0px";
  const effectiveHeight = `calc(${height} - (${bannerSpace}))`;
  const effectiveContentHeight = `calc(${contentHeight} - (${bannerSpace}))`;

  const handleViewInvoice = (invoice: IBoxStorageHistoryInvoice) => {
    setSelectedInvoice(invoice);
    setOpenModal(true);
  };

  const styles = {
    tableHeadingCell: {
      borderRadius: "0",
      background: Colors.SECONDARY,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      padding: "0.5rem 0.71rem",
      boxSizing: "border-box",
      color: "white",
      fontWeight: 600,
      height: "2.92857142857rem",
    },
  };

  return (
    <>
      {pendingAssignmentBills.length > 0 && (
        <Box
          ref={bannerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            background: `${Colors.SECONDARY}0D`,
            border: `1px solid ${Colors.SECONDARY}40`,
            borderRadius: "0.93rem",
            padding: "0.85rem 1rem",
            marginBottom: "0.71rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: Colors.SECONDARY,
                fontSize: "0.95rem",
              }}
            >
              Invoices selected for box assignment
            </Typography>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "0.8rem",
                color: Colors.SECONDARY,
                background: Colors.WHITE,
                borderRadius: "999px",
                padding: "0.14rem 0.64rem",
              }}
            >
              {pendingAssignmentBills.length} invoice
              {pendingAssignmentBills.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {pendingAssignmentBills.map((bill) => (
              <Box
                key={bill.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.43rem",
                  background: Colors.WHITE,
                  border: `1px solid ${Colors.ACCENT_GRAY}`,
                  borderRadius: "0.71rem",
                  padding: "0.36rem 0.71rem",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: Colors.SECONDARY,
                    fontSize: "0.85rem",
                  }}
                >
                  {bill.billId}
                </Typography>
                <Typography
                  sx={{ color: Colors.MAIN_GRAY, fontSize: "0.8rem" }}
                >
                  {bill.totalPieceCount ?? bill.totalItemCount} pcs
                </Typography>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    color: Colors.TEXT_GRAY_DARK,
                    fontSize: "0.85rem",
                  }}
                >
                  {formatCurrency({ amount: bill.billAmount, prefix: "$" })}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          background: Colors.WHITE,
          border: `1px solid ${Colors.ACCENT_GRAY}`,
          borderRadius: "0.93rem",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          overflow: "hidden",
          gap: "1.14rem",
          height: effectiveHeight,
          transition: "height 0.15s ease",
        }}
      >
      <Box
        sx={{
          flex: 4,
          minWidth: "100%",
          maxWidth: "100% !important",
          overflowX: "hidden",
          overflowY: "hidden",
        }}
      >
        <Grid container sx={{ gap: "0.21rem", height: "100%" }}>
          <Grid item xs={12}>
            <Box
              style={{
                position: "sticky",
                top: 0,
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
              }}
            >
              <Grid container sx={{ gap: "0.15rem", flexWrap: "nowrap" }}>
                <Grid
                  item
                  xs={1}
                  sx={{
                    ...styles.tableHeadingCell,
                    minWidth: "2.92857142857rem",
                    maxWidth: "2.92857142857rem !important",
                    borderTopLeftRadius: "0.71rem",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    indeterminate={
                      selectedIds.size > 0 && selectedIds.size < rows.length
                    }
                    onChange={toggleSelectAll}
                    sx={{
                      color: Colors.WHITE,
                      "&.Mui-checked": { color: Colors.WHITE },
                      "&.MuiCheckbox-indeterminate": { color: Colors.WHITE },
                    }}
                  />
                </Grid>
                {[
                  { label: "Box No", xs: 4, width: "6.4rem" },
                  { label: "Date In", xs: 4, width: "6.8rem" },
                  { label: "Cashier", xs: 2.5, width: "5.5rem" },
                  { label: "Due date", xs: 4, width: "6.5rem" },
                  { label: "Pieces", xs: 2.5, width: "5.606rem" },
                  { label: "Rack", xs: 2.5, width: "4.606rem" },
                  { label: "Balance", xs: 4, width: "5.5rem" },
                  { label: "Pay Date", xs: 4, width: "6.5rem" },
                  { label: "Pickup Date", xs: 4, width: "6.5rem" },
                  { label: "Status", xs: 2.5, width: "6.2rem" },
                ].map((col) => (
                  <Grid
                    key={col.label}
                    item
                    xs={col.xs}
                    sx={{
                      ...styles.tableHeadingCell,
                      minWidth: col.width,
                      flexGrow: 1,
                      fontSize: "1rem",
                    }}
                  >
                    {col.label}
                  </Grid>
                ))}
                <Grid
                  item
                  xs={1}
                  sx={{
                    ...styles.tableHeadingCell,
                    minWidth: "5.246rem",
                    maxWidth: "5.246rem !important",
                    borderTopRightRadius: "0.71rem",
                  }}
                >
                  &nbsp;
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ height: effectiveContentHeight }}>
            <Box
              sx={{
                height: effectiveContentHeight,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: { lg: "0.5rem", md: "0.3rem", sm: "0rem" },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ADADAD",
                  borderRadius: "0rem",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                  borderRadius: "0rem",
                },
              }}
            >
              {!loading &&
                rows.length > 0 &&
                rows.map((row) => (
                  <BoxStorageHistoryTableRow
                    key={row.configId}
                    data={row}
                    expanded={expandedIds.has(row.configId)}
                    onToggleExpand={toggleExpand}
                    selected={selectedIds.has(row.configId)}
                    onToggleSelect={toggleSelect}
                    onViewInvoice={handleViewInvoice}
                  />
                ))}

              {!loading && rows.length === 0 && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    py: "1rem",
                    color: Colors.SECONDARY,
                  }}
                >
                  No storage boxes found
                </Box>
              )}

              {loading && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress sx={{ mx: "auto", mt: "7rem" }} />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {selectedInvoice && (
        <InvoiceHistoryModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          selectedId={selectedInvoice.id}
          billId={selectedInvoice.billId ?? ""}
          customerId={customerId ?? ""}
        />
      )}
      </Box>
    </>
  );
};

export default BoxStorageHistoryTable;
